import React from 'react';
import { motion } from 'framer-motion';
import './NatureCard.css';

interface NatureCardProps {
  children: React.ReactNode;
  className?: string;
  organic?: boolean;
  hoverable?: boolean;
  onClick?: () => void;
  gradient?: 'forest' | 'earth' | 'gold' | 'none';
  delay?: number;
}

/**
 * NatureCard - A botanical-themed card component
 * Features organic shapes, natural shadows, and smooth animations
 */
export const NatureCard: React.FC<NatureCardProps> = ({
  children,
  className = '',
  organic = false,
  hoverable = true,
  onClick,
  gradient = 'none',
  delay = 0,
}) => {
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: delay,
        ease: "easeOut" as const,
      },
    },
  };

  const hoverVariants = hoverable
    ? {
        scale: 1.02,
        y: -4,
        transition: {
          duration: 0.3,
          ease: "easeOut" as const,
        },
      }
    : {};

  const classes = [
    'nature-card',
    organic && 'nature-card--organic',
    hoverable && 'nature-card--hoverable',
    gradient !== 'none' && `nature-card--gradient-${gradient}`,
    onClick && 'nature-card--clickable',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <motion.div
      className={classes}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover={hoverVariants}
      onClick={onClick}
    >
      {children}
    </motion.div>
  );
};

export default NatureCard;
